import { useTranslation } from 'react-i18next'
import React from 'react'
import { useFormContext } from 'react-hook-form'
import {
  TransportEquipmentType,
} from 'features/declaration/models/schemas/commonConsignmentSchemas'
import { HouseConsignmentFormValues } from 'features/declaration/house-consignment/models/schema'
import useSoftDeleteFieldArray from 'shared/rhf/useSoftDeleteFieldArray'
import useCodelist from 'features/declaration/codelist/api/useCodelist'
import { blankTransportEquipment } from 'features/declaration/models/blankItems'
import FormCollapsibleColumn from '../../../../components/p5/FormCollapsibleColumn'
import FormField from '../../../../components/p5/FormField'
import Input from '../../../../components/p5/Input'
import FormFieldArray from '../../../../components/p5/FormFieldArray'
import FormSelect from '../../../../components/p5/FormSelect'
import Seals from './Seals'
import RadioGroup from '../../../../components/p5/RadioGroup'
import useDeclarationFieldRequirements from '../../form/components/useDeclarationFieldRequirements'

const scope = 'transportEquipment'

function TransportEquipment() {
  const { t } = useTranslation()

  const { control } = useFormContext<HouseConsignmentFormValues>()

  const {
    softRemove,
    appendWithSequence,
    visibleFields,
  } = useSoftDeleteFieldArray<HouseConsignmentFormValues, TransportEquipmentType>({
    control,
    name: 'transportEquipment',
  })

  const [, containerSizeAndIdentificationOptions] = useCodelist('CONTAINER_SIZE_AND_TYPE_IDENTIFICATIONS')
  const [, containerPackedStatusOptions] = useCodelist('CONTAINER_PACKED_STATUSES')
  const [, containerSupplierTypeOptions] = useCodelist('CONTAINER_SUPPLIER_TYPES')

  const fieldRequirements = useDeclarationFieldRequirements<HouseConsignmentFormValues>('houseConsignment', [
    scope,
    'containerIndicator',
  ])
  const isAddForbidden = fieldRequirements[scope]?.requirement === 'forbidden'

  return (
    <section className="py-3">
      <FormCollapsibleColumn
        columnId="declaration-transport-equipment-section"
        headingLabel={t('declaration.p5.transportEquipment')}
        fieldRule={fieldRequirements[scope]}
      >
        <FormField
          labelKey="declaration.p5.containerisedIndicator"
          fieldRule={fieldRequirements.containerIndicator}
        >
          <RadioGroup
            field="containerIndicator"
            radioOptions={[
              { label: 'common.no', value: 'false' },
              { label: 'common.yes', value: 'true' },
            ]}
            fieldRule={fieldRequirements.containerIndicator}
          />
        </FormField>
        {visibleFields.map((fieldWithIndex) => {
          const equipmentItem = fieldWithIndex.field
          const innerItemScope = `transportEquipment.${fieldWithIndex.index}` as const

          return (
            <FormFieldArray
              key={`equipmentItem_${equipmentItem.id}`}
              titleKey="declaration.p5.sequenceNumber"
              sequenceNumber={equipmentItem.sequenceNumber + 1}
              removeItem={() => softRemove(fieldWithIndex.index)}
            >
              <div className="equipment-fields">
                <FormField labelKey="declaration.p5.containerNumber">
                  <Input
                    field={`${innerItemScope}.containerIdentificationNumber`}
                    labelKey="declaration.p5.containerNumber"
                    type="text"
                    placeholder={t('common.fieldPlaceholder')}
                  />
                </FormField>
                <FormField labelKey="declaration.p5.containerSizeAndType">
                  <FormSelect
                    field={`${innerItemScope}.containerSizeAndType`}
                    labelKey="declaration.p5.containerSizeAndType"
                    options={containerSizeAndIdentificationOptions}
                    type="sync"
                  />
                </FormField>
                <FormField labelKey="declaration.p5.containerPackedStatus">
                  <FormSelect
                    field={`${innerItemScope}.packedStatus`}
                    labelKey="declaration.p5.containerPackedStatus"
                    options={containerPackedStatusOptions}
                    type="sync"
                  />
                </FormField>
                <FormField labelKey="declaration.p5.containerSupplierType">
                  <FormSelect
                    field={`${innerItemScope}.supplierType`}
                    labelKey="declaration.p5.containerSupplierType"
                    options={containerSupplierTypeOptions}
                    type="sync"
                  />
                </FormField>
              </div>
              <Seals transportEquipmentIndex={fieldWithIndex.index} />
            </FormFieldArray>
          )
        })}
        <div className="row">
          <div className={`col-12 text-end ${isAddForbidden ? 'cursor--not-allowed' : ''}`}>
            <button
              type="button"
              className="btn btn-sm btn-primary mt-2"
              onClick={() => appendWithSequence(blankTransportEquipment)}
              disabled={isAddForbidden}
            >
              <i className="fa fa-plus me-2" />
              {t('declaration.p5.addNewContainer')}
            </button>
          </div>
        </div>
      </FormCollapsibleColumn>
    </section>
  )
}

export default TransportEquipment
